import { useEffect, useState } from 'react';
import { ApiError, createAdminSession, getAdminSession } from '../lib/api';
import type { AdminSession } from '../lib/types';
import { AdminLayout } from './AdminLayout';
import { DashboardPage } from './pages/DashboardPage';
import { EngagementsAdmin } from './pages/EngagementsAdmin';
import { InquiriesAdmin } from './pages/InquiriesAdmin';
import { PagesAdmin } from './pages/PagesAdmin';
import { MediaAdmin } from './pages/MediaAdmin';
import './admin.css';

interface AdminAppProps {
  navigateTo?: (path: string) => void;
}

export function AdminApp({ navigateTo = (path) => window.location.assign(path) }: AdminAppProps = {}) {
  type AuthState =
    | { mode: 'loading' }
    | { mode: 'error'; message: string }
    | { mode: 'authenticated'; session: AdminSession };

  const [authState, setAuthState] = useState<AuthState>({ mode: 'loading' });

  async function loadAuthentication() {
    setAuthState({ mode: 'loading' });
    try {
      setAuthState({ mode: 'authenticated', session: await getAdminSession() });
    } catch (sessionError) {
      if (!(sessionError instanceof ApiError) || sessionError.status !== 401) {
        setAuthState({ mode: 'error', message: sessionError instanceof Error ? sessionError.message : 'Unable to verify your session.' });
        return;
      }

      try {
        setAuthState({ mode: 'authenticated', session: await createAdminSession() });
      } catch (createError) {
        setAuthState({ mode: 'error', message: createError instanceof Error ? createError.message : 'Unable to establish your session.' });
      }
    }
  }

  useEffect(() => {
    void loadAuthentication();
    // Authentication is intentionally checked only when this application mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (authState.mode === 'loading') return <main className="admin-loading" aria-label="Loading admin"><h1>Admin</h1><p>Verifying your secure session…</p></main>;
  if (authState.mode === 'error') return <main className="admin-auth"><section className="admin-auth__card"><p className="admin-kicker">Christina J Barea</p><h1>Admin unavailable</h1><div className="admin-error" role="alert">{authState.message}</div><button type="button" onClick={() => void loadAuthentication()}>Try again</button></section></main>;
  const { session } = authState;

  const path = window.location.pathname.replace(/\/$/, '');
  const page = path === '/admin/engagements' ? <EngagementsAdmin csrfToken={session.csrfToken} />
    : path === '/admin/inquiries' ? <InquiriesAdmin csrfToken={session.csrfToken} />
      : path === '/admin/pages' ? <PagesAdmin csrfToken={session.csrfToken} />
        : path === '/admin/media' ? <MediaAdmin csrfToken={session.csrfToken} /> : <DashboardPage />;

  return (
    <AdminLayout session={session} onSignedOut={() => navigateTo('/cdn-cgi/access/logout')}>
      {page}
    </AdminLayout>
  );
}
